import React, { useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import { PROVIDER_STRINGS, COMMON_STRINGS } from "../../constants/strings";

import { getProviders, updateProviderStatus } from "../../services/api";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

// 🔥 TYPES
import { ProviderRow } from "../../types";

import "../table-common.css";

const ProviderListPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [statusFilter, setStatusFilter] = useState<ProviderRow["status"] | "">(
    "",
  );

  // ================= FETCH =================
  const fetchProviders = async () => {
    try {
      setLoading(true);

      const res = await getProviders();

      const formatted: ProviderRow[] = (res.data.data || []).map((p: any) => ({
        _id: p._id,
        name: p.name,
        mobile: p.mobile,
        city: p.city,
        category: p.category,
        status: (p.providerStatus || "pending") as ProviderRow["status"],
      }));

      setRows([...formatted].reverse());
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  // ================= UPDATE =================
  const handleStatusChange = async (
    id: string,
    newStatus: ProviderRow["status"],
  ) => {
    try {
      setRows((prev) =>
        prev.map((row) =>
          row._id === id ? { ...row, status: newStatus } : row,
        ),
      );

      await updateProviderStatus(id, newStatus);

      dispatch(
        showSnackbar({
          message: "Provider status updated successfully",
          type: "success",
        }),
      );
    } catch {
      fetchProviders();
    }
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();

    const searchMatch =
      row.name?.toLowerCase().includes(search) ||
      row.mobile?.includes(searchText) ||
      row.city?.toLowerCase().includes(search) ||
      row.category?.toLowerCase().includes(search);

    const statusMatch = statusFilter ? row.status === statusFilter : true;

    return searchMatch && statusMatch;
  });

  // ================= COLUMNS =================
  const columns: GridColDef<ProviderRow>[] = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "mobile", headerName: "Mobile", flex: 1 },
    { field: "city", headerName: "City", flex: 1 },
    { field: "category", headerName: "Service", flex: 1 },

    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },

    {
      field: "actions",
      headerName: "Actions",
      flex: 1.8,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => {
        const row = params.row;

        const isApproved = row.status === "approved";
        const isRejected = row.status === "rejected";

        return (
          <Box className="action-cell">
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckCircleIcon />}
                disabled={isApproved}
                onClick={() => handleStatusChange(row._id, "approved")}
                sx={{
                  backgroundColor: isApproved ? "#d1d5db" : "#22c55e",
                  color: isApproved ? "#888" : "#fff",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Approve
              </Button>

              <Button
                size="small"
                variant="outlined"
                startIcon={<BlockIcon />}
                disabled={isRejected}
                onClick={() => handleStatusChange(row._id, "rejected")}
                sx={{
                  color: isRejected ? "#aaa" : "#ef4444",
                  borderColor: isRejected ? "#ddd" : "#ef4444",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Reject
              </Button>
            </Stack>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={PROVIDER_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder={PROVIDER_STRINGS.SEARCH_PLACEHOLDER}
        filterValue={statusFilter}
        onFilterChange={(value) =>
          setStatusFilter(value as ProviderRow["status"] | "")
        }
        filterOptions={[
          { label: "All", value: "" },
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
          { label: "Rejected", value: "rejected" },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />
    </Box>
  );
};

export default ProviderListPage;
